/**
 * StudiesCard — every study this lead has ever been considered for, with an
 * independent status on each (multi-study lead lists, plan §6.2).
 *
 * Owner request 2026-08-14: "Each study should have its own list that is a
 * history of anyone who has been considered for that study and their current
 * status for it. Leads can technically be considered for multiple studies at
 * the same time."
 *
 * The lead's pipeline stage (contacts.lifecycle_status) still describes the
 * FOCUS study — the one marked here — so changing a non-focus row's status
 * moves that study's list only and never touches the funnel position shown in
 * the header. "Work this study" is the one action that moves the focus, and it
 * is a pure re-point: the membership already exists either way.
 *
 * Rows are never removed. A lead DQ'd from a study keeps their row at its
 * terminal status; that IS the history the owner asked for.
 *
 * Each row names its own recruiter (owner request 2026-09-29; migration
 * 20260929190000): a lead who comes back through another study's form goes to
 * that study's recruiters and stays with their recruiter on the first study.
 * The focus row's recruiter is the lead's main one (the header's).
 *
 * The "randomized here, still in play there" warning used to sit inside this
 * card, which meant it was only visible on the Screening tab. It now lives in
 * RandomizedConflictBanner below and is rendered ONCE by LeadRail, above the tab
 * strip — see the note there. There is deliberately no second copy in the card.
 */
import { useMemo, useState } from 'react';
import { FlaskConical, Plus, Star, AlertTriangle, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { ContactStudyMembership, StudyOption, studyInternalLabel } from '../types';
import { MEMBERSHIP_STATUSES, CONSIDERING_LIFECYCLES, lifecycleMeta, relTime } from '../lib';
import LifecycleSelect from './LifecycleSelect';
import OwnerSelect from './OwnerSelect';
import RandomizedConflictModal from './RandomizedConflictModal';

/**
 * The "enrolled here, still being worked there" warning (plan §6.2 / §6.4).
 *
 * L4 — lifted out of the Studies card so LeadRail can render it above the tab
 * strip: a recruiter sitting on Next steps was about to text, book or hand to
 * the agent a lead who is already randomized somewhere, and saw nothing. Not an
 * error (recruiters line up follow-on studies deliberately) but it must never be
 * a surprise, so it is stated plainly and persistently on every tab.
 *
 * Shows when the lead holds a `randomized` membership AND either another study
 * is still actively in play, or some membership carries the guard's override
 * stamp. Returns null otherwise, so callers render it unconditionally.
 *
 * No margin of its own — the rail is a flex column with its own gap, and this
 * has exactly one caller.
 */
export function RandomizedConflictBanner({ memberships }: { memberships?: ContactStudyMembership[] | null }) {
  const rows = memberships || [];
  const randomized = rows.find((m) => m.status === 'randomized');
  const alsoInPlay = rows.filter(
    (m) => m.study.id !== randomized?.study.id && CONSIDERING_LIFECYCLES.includes(m.status)
  );
  const stamped = rows.filter((m) => m.metadata?.randomized_conflict);
  if (!randomized || (alsoInPlay.length === 0 && stamped.length === 0)) return null;

  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <span>
        Randomized in <span className="font-semibold">{randomized.study.title || 'a study'}</span>
        {alsoInPlay.length > 0 && (
          <> — also in play for{' '}
            <span className="font-semibold">
              {alsoInPlay.map((m) => m.study.title || m.study.slug || 'a study').join(', ')}
            </span>
          </>
        )}
        .
      </span>
    </div>
  );
}

interface Props {
  contactId: string;
  memberships: ContactStudyMembership[];
  /** CRM studies available to add (lead-only stubs have no id and are excluded). */
  studies: StudyOption[];
  /** Refetch the lead — a membership write can also move the focus/stage. */
  onChanged: () => void;
}

export default function StudiesCard({ contactId, memberships, studies, onChanged }: Props) {
  const [adding, setAdding] = useState(false);
  const [addStudyId, setAddStudyId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A 409 randomized_conflict on add / work-this-study, with the request
  // stashed so confirming re-sends it with the acknowledge flag (plan §6.4).
  const [conflict, setConflict] = useState<{
    studyId: string;
    setFocus: boolean;
    targetStudyTitle?: string | null;
    randomizedStudyTitle?: string | null;
    randomizedAt?: string | null;
  } | null>(null);

  const studyTitleFor = (studyId: string) =>
    memberships.find((m) => m.study.id === studyId)?.study.title
    || studies.find((st) => st.id === studyId)?.title
    || null;

  /** POST the membership, surfacing a 409 as the acknowledgement modal. */
  const postStudy = async (studyId: string, setFocus: boolean, acknowledged = false) => {
    await api.post(`/api/crm/contacts/${contactId}/studies`, {
      study_id: studyId,
      ...(setFocus ? { set_focus: true } : {}),
      ...(acknowledged ? { randomized_conflict_acknowledged: true } : {}),
    });
  };

  const handleWriteError = (e: any, studyId: string, setFocus: boolean, fallback: string) => {
    const data = e?.response?.data;
    if (e?.response?.status === 409 && data?.reason === 'randomized_conflict') {
      setConflict({
        studyId,
        setFocus,
        targetStudyTitle: studyTitleFor(studyId),
        randomizedStudyTitle: data.randomized_study?.title ?? null,
        randomizedAt: data.randomized_at ?? null,
      });
      return;
    }
    setError(data?.error || fallback);
  };

  const memberStudyIds = useMemo(
    () => new Set(memberships.map((m) => m.study.id)),
    [memberships]
  );
  // Only studies they're not already on — adding a duplicate is a no-op anyway.
  const addable = useMemo(
    () => studies.filter((s) => s.id && s.source === 'crm' && !memberStudyIds.has(s.id)),
    [studies, memberStudyIds]
  );

  const addToStudy = async () => {
    if (!addStudyId) return;
    setBusy('add');
    setError(null);
    try {
      await postStudy(addStudyId, false);
      setAdding(false);
      setAddStudyId('');
      onChanged();
    } catch (e: any) {
      handleWriteError(e, addStudyId, false, 'Could not add this lead to that study.');
    } finally {
      setBusy(null);
    }
  };

  const workThisStudy = async (studyId: string) => {
    setBusy(studyId);
    setError(null);
    try {
      await postStudy(studyId, true);
      onChanged();
    } catch (e: any) {
      handleWriteError(e, studyId, true, 'Could not move the focus to that study.');
    } finally {
      setBusy(null);
    }
  };

  /** Re-send the request the guard blocked, this time acknowledged. */
  const confirmConflict = async () => {
    if (!conflict) return;
    const { studyId, setFocus } = conflict;
    setConflict(null);
    setBusy(setFocus ? studyId : 'add');
    setError(null);
    try {
      await postStudy(studyId, setFocus, true);
      if (!setFocus) { setAdding(false); setAddStudyId(''); }
      onChanged();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not update that study.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {conflict && (
        <RandomizedConflictModal
          targetStudyTitle={conflict.targetStudyTitle}
          randomizedStudyTitle={conflict.randomizedStudyTitle}
          randomizedAt={conflict.randomizedAt}
          onCancel={() => setConflict(null)}
          onConfirm={confirmConflict}
        />
      )}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <FlaskConical className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
          Studies
          {memberships.length > 0 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
              ({memberships.length})
            </span>
          )}
        </h3>
        {addable.length > 0 && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <Plus className="h-4 w-4" /> Add to study
          </button>
        )}
      </div>

      {/* NO randomized-conflict banner here on purpose (L4): LeadRail renders
          RandomizedConflictBanner above the tab strip, so it is visible on every
          tab instead of only this one. Two copies would double it on Screening. */}

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {adding && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select
            value={addStudyId}
            onChange={(e) => setAddStudyId(e.target.value)}
            aria-label="Study to add"
            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">Select a study…</option>
            {addable.map((s) => (
              <option key={s.id} value={s.id!}>{studyInternalLabel(s)}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={!addStudyId || busy === 'add'}
            onClick={addToStudy}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {busy === 'add' && <Loader2 className="h-4 w-4 animate-spin" />} Add
          </button>
          <button
            type="button"
            onClick={() => { setAdding(false); setAddStudyId(''); setError(null); }}
            className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Cancel
          </button>
        </div>
      )}

      {memberships.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Not on any study list yet.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {memberships.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {m.study.title || m.study.slug || 'Untitled study'}
                  </span>
                  {m.is_focus && (
                    <span
                      title="The study this lead is currently being worked for — their pipeline stage describes this one"
                      className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                    >
                      <Star className="h-3 w-3" /> Focus
                    </span>
                  )}
                  {m.metadata?.randomized_conflict && (
                    <AlertTriangle
                      className="h-3.5 w-3.5 flex-shrink-0 text-amber-500"
                      aria-label="Added while randomized in another study"
                    />
                  )}
                </div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Considered {relTime(m.first_considered_at)}
                  {' · '}
                  {lifecycleMeta(m.status).label} {relTime(m.status_changed_at)}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Recruiter</span>
                  <OwnerSelect
                    key={`${m.study.id}-${m.owner_user_id ?? 'unassigned'}`}
                    contactId={contactId}
                    value={m.owner_user_id ?? null}
                    studyId={m.study.id}
                    ariaLabel={`Recruiter for ${m.study.title || m.study.slug || 'this study'}`}
                    compact
                    onChanged={() => onChanged()}
                  />
                </span>
                {!m.is_focus && (
                  <button
                    type="button"
                    disabled={busy === m.study.id}
                    onClick={() => workThisStudy(m.study.id)}
                    title="Make this the study the lead is being worked for"
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                  >
                    {busy === m.study.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    Work this study
                  </button>
                )}
                <LifecycleSelect
                  contactId={contactId}
                  value={m.status}
                  statuses={MEMBERSHIP_STATUSES}
                  primaryStudyId={m.study.id}
                  commit={async (next, extra) => {
                    await api.patch(
                      `/api/crm/contacts/${contactId}/studies/${m.study.id}`,
                      { status: next, ...extra }
                    );
                    onChanged();
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
